import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetchWrapper } from "../hooks/useFetchWrapper";
import { useLocalStorageNew } from "../hooks/useLocalStorageNew";
import { useLoading } from "../utils/LoadingContext";
import {
    AUTHORIZATION_TYPE,
    CSM_HOME_PATH,
    HOME_PATH,
    IDENTITY_PROVIDER_TYPES,
    INTEGRATION_TYPES,
    LOGIN_PATH,
    POLL_CHOOSER_PATH,
    ROLES,
    VIEWER_USER_TYPE
} from "../utils/constants";
import {
    getShortName,
    logoutOfChromeExtensions,
    notifyError,
    notifySuccess,
    sendMsgToExtension
} from "../utils/helpers";
import { CourseApiCalls } from './CourseApiCalls';
import { PollApiCalls } from "./PollApiCalls";
import { ResponseApiCalls } from "./ResponseApiCalls";
import {useConfig} from "../hooks/useConfig";
import {useCSMAccessCheck} from "../hooks/useCSMAccessCheck";
import {useCanvasOAuth} from "../hooks/useCanvasOAuth";

export const AuthenticationApiCalls = () => {
    const { t } = useTranslation();
    const fetchWrapper = useFetchWrapper();
    const config = useConfig();
    const [setSession, getSession] = useLocalStorageNew("session", {});
    const [, getHostResource] = useLocalStorageNew(window.location.hostname, {});
    const [setWarning, getWarning ] = useLocalStorageNew("warningShowed", {});
    const history = useHistory();
    const {ensureLMSAuthorized} = useCanvasOAuth();

    const {setUserNameToLocalStorage} = ResponseApiCalls();
    const { getPollRecordFromUniqueCode } = PollApiCalls();
    const { checkMemberAccess } = CourseApiCalls();

    const location = useLocation();
    const {setLoading} = useLoading();
    const {isCSMSystem} = useCSMAccessCheck();

    const checkUserAccess = async () => {
        const result = await getPollRecordFromUniqueCode(location.state.pollCode);
        if(!result || !result.poll) {
          return false; 
        }
        const courseId = result.poll.index2Pk;
        if (courseId == null) {
            console.log("This poll has no associated course");
            return true;
        }
    
        const accessibleResult = await checkMemberAccess(courseId.substring(2));
        console.log(accessibleResult)
        if (accessibleResult == null || accessibleResult.result == null || !accessibleResult.result) {
            return false; 
        }
    
        return true;
      }

      
    return {
        login,
        logout,
        reLogin,
        sendResetPwdEmail,
        resetPwd,
        getSAMLRedirectURL,
        loginSAML,
        loginCANVAS,
        loginBB,
        loginMOODLE,
        loginD2L,
        createUserRoster,
        testLmsApiAccess,
        getD2LAuthURL,
        getAuthCount,
        switchInst,
        loginCSM,
        saveMGMTSettings,
        countLMSLaunch,
        postLoginMessageToExtension,
        tryCanvasURL
        // storeD2LAuthToken
    }

    function postLoginMessageToExtension(res) {
        const { token,  institutionId} = res;
        let shortName = getShortName();
        const messageObj = {
            type: "ENGAGE_AUTH_LOGIN",
            zone: config.zone,
            shortName: shortName,
            institutionId: institutionId,
            token: token
        };

        sendMsgToExtension(config.chrome_extension_id, messageObj);
        window.postMessage(messageObj, "*");
    }

    function postLogoutMessageToExtension() {
        const messageObj = {type: "ENGAGE_AUTH_LOGOUT"};
        sendMsgToExtension(config.chrome_extension_id, messageObj);
        window.postMessage(messageObj, "*");
    }

    // Function that handles login
    // Does not return any data, sets all the session data in the function
    function login(user, cb) {
        const userName = user.userName;
        const bodyObj = Object.assign({}, user);

        const {institutionId} = getHostResource();
        bodyObj.institutionId = institutionId;
        // set user in local storage, and go back to the previous page
        return fetchWrapper.post(
            {
                url: `${config.api.authentication}/login`,
                body: bodyObj,
                authorizeRequired: false
            }
        ).then(res => {
            const {result={}, success=true, errorMessage} = res;
            if (!success) {
                notifyError(errorMessage);
            }

            const {resetCode, userName: name, token, role, userId, firstName, lastName, email, idpType, settings} = result;
            if (role !== ROLES.VIEWER.value) {
                postLoginMessageToExtension(result);
            }

            if (!!resetCode) {
                history.push(`/reset/${name}/${resetCode}`, {from: location.pathname, loginRedirectState: location.state});
                return;
            }

            const session = getSession();
            session.authorization = token;
            session.userName = !!name ? name : userName;
            session.role = role;
            session.userId = userId;
            session.firstName = firstName ? firstName : "";
            session.lastName = lastName ? lastName : "";
            session.email = email ? email : "";
            // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.YUJA;
            session.isYuJaAuto = idpType === "YuJa_Auto";
            session.idpType = IDENTITY_PROVIDER_TYPES.YUJA;
            session.gradedLink = false;
            session.settings = settings ? settings : {};
            if (result.hasOwnProperty("isFromCSM")) {
                session.isFromCSM = result.isFromCSM;
            }

            if (result.hasOwnProperty("isCSMAdmin")) {
                session.isCSMAdmin = result.isCSMAdmin;
            }

            if (result.hasOwnProperty("MGMTSettings")) {
                session.MGMTSettings = result.MGMTSettings;
            }

            let warning = getWarning();
            if (!warning || userId != Object.keys(warning)[0]) {
                setWarning({[userId]: false}); 
            }
            if (location.state
                && location.state.pollCode
                && location.state.from
                && location.state.from.includes("join")) {
                setSession(session);
                // checkUserAccess().then((userHasAccess) => {
                //     if (!userHasAccess) {
                //         history.push(HOME_PATH);
                //     }
                // }).catch((error) => {console.error(error);});
                setUserNameToLocalStorage(location.state.pollCode, userId, VIEWER_USER_TYPE.LOGGED_IN);
            }
            // console.log(session);
            setSession(session);
            if (location.state && location.state.from) {
                if(location.state.from === LOGIN_PATH){
                    history.push(!isCSMSystem() ? HOME_PATH : CSM_HOME_PATH);
                } else {
                    history.push(location.state.from);
                }
            } else {
                let str = !isCSMSystem() ? HOME_PATH : CSM_HOME_PATH;
                 history.push(str);
            } 
        }).catch(err => {
            let msgObj = !!err.message ? JSON.parse(err.message) : {};
            if (!!cb) {
                cb(msgObj.errorCode);
                return;
            }

            if (msgObj.errorCode === "Y022") {
                history.push(LOGIN_PATH, {msg: t('auth.userNotFound'), from: location.pathname});
            } else if (msgObj.errorCode === "Y023") {
                history.push(LOGIN_PATH, {msg: t('auth.invalidCredentials'), from: location.pathname});
            } else {
                history.push(LOGIN_PATH, {msg: t('auth.generalLoginError'), from: location.pathname});
            }
        });
    }

    // Function that handles logout 
    // Does not return any data 
    function logout() {
        // remove user from local storage, set auth state to null and redirect to home page
        setLoading(true);
        const session = getSession();
        logoutOfChromeExtensions();
        return fetchWrapper.post(
            {
                 url: `${config.api.authentication}/logout`,
                 body: {
                    "secret" : session.secret,
                    "intType": session.intType
                }
            }).then(result => {
                if (result.success) {
                    postLogoutMessageToExtension();
                    if(result.result && result.result.remoteLogoutUrl) {
                        setSession({});
                        // return fetchWrapper.get({url: result.result.remoteLogoutUrl}, {}, false, false, {});
                        // history.push(result.result.remoteLogoutUrl);
                        window.location.replace(result.result.remoteLogoutUrl);
                    } else {
                        setSession({});
                        history.push(LOGIN_PATH);
                    }
                } else {
                    notifyError(result.errorMessage);
                }
            }).catch(err => {
                console.log(err);
                notifyError(t('common.serverError'));
            }).finally(() => {
                setLoading(false);
            })
    }

    function reLogin() {
        return fetchWrapper.post({url: `${config.api.authentication}/reLogin/`})
            .then(result => {
                if (result.success) {
                    postLoginMessageToExtension(result.result);
                    const session = getSession();
                    session.authorization = result.result.token;
                    session.userName = result.result.userName;
                    session.role = result.result.role;
                    session.userId = result.result.userId;
                    // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.YUJA;
                    session.idpType = IDENTITY_PROVIDER_TYPES.YUJA;
                    session.gradedLink = false;
                    session.settings = result.result.settings ? result.result.settings : {};
                    if (result.result.hasOwnProperty("isFromCSM")) {
                        session.isFromCSM = result.result.isFromCSM;
                    }

                    if (result.result.hasOwnProperty("isCSMAdmin")) {
                        session.isCSMAdmin = result.result.isCSMAdmin;
                    }

                    if (result.result.hasOwnProperty("MGMTSettings")) {
                        session.MGMTSettings = result.result.MGMTSettings;
                    }

                    setSession(session);
                } else {
                    notifyError(result.errorMessage);
                    history.push(LOGIN_PATH, {from: location.pathname});
                }
            }).catch(err => {
                console.log(err);
                notifyError(t('common.serverError'));
            })
    }


    function sendResetPwdEmail(identity) {
        const {institutionId} = getHostResource();
        return fetchWrapper.post({
            url: `${config.api.authentication}/pwd/email`,
            body: {institutionId, identity},
            authorizeRequired: false
        }).then(result => {
            if (!result.success) {
                notifyError(result.errorMessage);
                return false;
            } else {
                return true;
            }
        }).catch(err => {
            if (JSON.parse(err.message).errorCode === "Y017") {
                notifyError(t('auth.emailNotFound'));
            } else if (JSON.parse(err.message).errorCode === "Y100") {
                notifyError(t('auth.invalidEmailFormat'));
            }
            else {
                notifyError(JSON.parse(err.message).errorMessage);
            }
            return false;
        });
    }

    function resetPwd(userName, resetCode, pwd) {
        return fetchWrapper.put({
            url: `${config.api.authentication}/pwd/${userName}/${resetCode}`,
            body: {pwd},
            authorizeRequired: false
        }).then(result => {
            if (!result.success) {
                notifyError(t('auth.resetPasswordError'));
                return false;
            } else {
                return true;
            }
        }).catch(err => {
            if (JSON.parse(err.message).errorCode === "Y017") {
                notifyError(t('auth.resetLinkExpired'));
            } else if (JSON.parse(err.message).errorCode === "Y032") {
                notifyError(t('auth.resetPasswordSameAsOld'));
            } else {
                notifyError(t('auth.resetPasswordError'));
            }

            return false;
        });
    }

    function getSAMLRedirectURL(ssoType) {
        return fetchWrapper.get({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/sso/saml/` + ssoType,
            body: {},
            authorizeRequired: false
        }).then(result => {
            if (!result.success) {
                notifyError(t('auth.ssoRedirectError'));
                return false;
            } else {
                return result.result.redirectURL;
            }
        }).catch(err => {
            notifyError(t('auth.ssoRedirectError'));
            return false;
        });
    }

    function loginSAML(secret, intType) {
        setLoading(true);
        return fetchWrapper.post({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/sso/saml`,
            body: {
                "secret": secret,
                "intType": intType
            },
            authorizeRequired: false
        }).then(result => {
            if (result.success) {
                postLoginMessageToExtension(result.result);
                const session = getSession();
                session.authorization = result.result.token;
                session.userName = result.result.userName;
                session.role = result.result.role;
                session.userId = result.result.userId;
                // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.SAML;
                session.idpType = IDENTITY_PROVIDER_TYPES.SAML;
                session.intType = intType; 
                session.firstName = result.result.firstName ? result.result.firstName : "";
                session.lastName = result.result.lastName ? result.result.lastName : "";
                session.email = result.result.email ? result.result.email : "";
                session.gradedLink = false;
                session.settings = result.result.settings ? result.result.settings : {};
                session.secret = secret;
                if (result.result.hasOwnProperty("isFromCSM")) {
                    session.isFromCSM = result.result.isFromCSM;
                }

                if (result.result.hasOwnProperty("isCSMAdmin")) {
                    session.isCSMAdmin = result.result.isCSMAdmin;
                }

                if (result.result.hasOwnProperty("MGMTSettings")) {
                    session.MGMTSettings = result.result.MGMTSettings;
                }

                setSession(session);
                if (location.state && location.state.from) {
                    history.push(location.state.from);
                } else {
                    history.push(!isCSMSystem() ? HOME_PATH : CSM_HOME_PATH);
                }
            } else {
                notifyError(result.errorMessage);
            }
        }).catch(err => {
            console.log(err);
            if (JSON.parse(err.message).errorCode === "Y026") {
                history.push(LOGIN_PATH);
            } else {
                history.push(LOGIN_PATH, {msg: t('auth.ssoRedirectError')});
            }
            return false;
        }).finally(() => {
            setLoading(false);
        })
    }

    function loginCANVAS(secret, intType, reqType, redirectURL) {
        setLoading(true);
        return fetchWrapper.post({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/lms`,
            body: {
                "secret": secret,
                "intType": intType
            },
            authorizeRequired: false
        }).then(result => {
            if (result.success) {
                postLoginMessageToExtension(result.result);
                const session = getSession();
                session.authorization = result.result.token;
                session.userName = result.result.userName;
                session.role = result.result.role;
                session.userId = result.result.userId;
                // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.LMS;
                session.idpType = IDENTITY_PROVIDER_TYPES.LMS;
                session.intType = INTEGRATION_TYPES.CANVAS;
                session.firstName = result.result.firstName ? result.result.firstName : "";
                session.lastName = result.result.lastName ? result.result.lastName : "";
                session.email = result.result.email ? result.result.email : "";
                session.gradedLink = false;
                session.settings = result.result.settings ? result.result.settings : {};
                if(result.result.courseId) {
                    session.courseId = result.result.courseId;
                }
                if(result.result.currentCourseRole && result.result.currentCourseRole.includes("#")) {
                    session.currentCourseRole = result.result.currentCourseRole.split("#")[1];
                } else {
                    session.currentCourseRole = "";
                }
                if(result.result.courses) {
                    session.courses = result.result.courses;
                }
                setSession(session);
                if(result.result.tpCourseId && result.result.tpCourseId !== "") {
                    const session = getSession();
                    session.tpCourseId = result.result.tpCourseId;
                    setSession(session);
                    // Roster sync now happens server-side inside loginLMS for every LMS type, so no
                    // separate createUserRoster call is needed here.
                }
                if(reqType && reqType === "deepLinking") {
                    history.push({pathname: POLL_CHOOSER_PATH + session.courseId, state : { secret : secret, intType : INTEGRATION_TYPES.CANVAS }});
                } else if(reqType && reqType === "pollResponseRedirect" && redirectURL !== "null") {
                    const session = getSession();
                    session.gradedLink = true;
                    if(session.currentCourseRole === ROLES.VIEWER.value || session.role === ROLES.VIEWER.value) {
                        setSession(session);
                        storeLTIGradebookRequest(secret, redirectURL.substring(0, 6));
                        setUserNameToLocalStorage(redirectURL.substring(0, 6), result.result.userId, VIEWER_USER_TYPE.LMS_CANVAS);
                        history.push({pathname: "/join/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    } else if(session.role === ROLES.CREATOR.value || session.role === ROLES.IT_MANAGER.value) {
                        setSession(session);
                        history.push({pathname: "/poll/share/" + redirectURL.slice(-36) + "/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    }
                } else {
                    if (location.state && location.state.from) {
                        history.push(location.state.from);
                    } else {
                        history.push(HOME_PATH);
                    }
                }
            } else {
                notifyError(result.errorMessage);
            }
        }).catch(err => {
            console.log(err);
            notifyError(t('auth.engageRedirectError'));
            return false;
        }).finally(() => {
            setLoading(false);
        })
    }

    function loginBB(secret, intType, reqType, redirectURL) {
        setLoading(true);
        return fetchWrapper.post({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/lms`,
            body: {
                "secret": secret,
                "intType": intType
            },
            authorizeRequired: false
        }).then(result => {
            if (result.success) {
                postLoginMessageToExtension(result.result);
                const session = getSession();
                session.authorization = result.result.token;
                session.userName = result.result.userName;
                session.role = result.result.role;
                session.userId = result.result.userId;
                // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.LMS;
                session.idpType = IDENTITY_PROVIDER_TYPES.LMS;
                session.intType = INTEGRATION_TYPES.BLACKBOARD;
                session.firstName = result.result.firstName ? result.result.firstName : "";
                session.lastName = result.result.lastName ? result.result.lastName : "";
                session.email = result.result.email ? result.result.email : "";
                session.gradedLink = false;
                session.settings = result.result.settings ? result.result.settings : {};
                if(result.result.courseId) {
                    session.courseId = result.result.courseId;
                }
                if(result.result.currentCourseRole && result.result.currentCourseRole.includes("#")) {
                    session.currentCourseRole = result.result.currentCourseRole.split("#")[1];
                } else {
                    session.currentCourseRole = "";
                }
                if(result.result.courses) {
                    session.courses = result.result.courses;
                }
                setSession(session);
                if(result.result.tpCourseId && result.result.tpCourseId !== "") {
                    const session = getSession();
                    session.tpCourseId = result.result.tpCourseId;
                    setSession(session);
                    // Roster sync now happens server-side inside loginLMS for every LMS type, so no
                    // separate createUserRoster call is needed here.
                }
                if(reqType && reqType === "deepLinking") {
                    history.push({pathname: POLL_CHOOSER_PATH + session.courseId, state : { secret : secret, intType : INTEGRATION_TYPES.BLACKBOARD }});
                } else if(reqType && reqType === "pollResponseRedirect" && redirectURL !== "null") {
                    const session = getSession();
                    session.gradedLink = true;
                    if(session.currentCourseRole === ROLES.VIEWER.value || session.role === ROLES.VIEWER.value) {
                        setSession(session);
                        storeLTIGradebookRequest(secret, redirectURL.substring(0, 6));
                        setUserNameToLocalStorage(redirectURL.substring(0, 6), result.result.userId, VIEWER_USER_TYPE.LMS_BLACKBOARD);
                        history.push({pathname: "/join/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    } else if(session.role === ROLES.CREATOR.value || session.role === ROLES.IT_MANAGER.value) {
                        setSession(session);
                        history.push({pathname: "/poll/share/" + redirectURL.slice(-36) + "/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    }
                } else {
                    if (location.state && location.state.from) {
                        history.push(location.state.from);
                    } else {
                        history.push(HOME_PATH);
                    }
                }
            } else {
                notifyError(result.errorMessage);
            }
        }).catch(err => {
            console.log(err);
            notifyError(t('auth.engageRedirectError'));
            return false;
        }).finally(() => {
            setLoading(false);
        })
    }

    function loginMOODLE(secret, intType, reqType, redirectURL) {
        setLoading(true);
        return fetchWrapper.post({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/lms`,
            body: {
                "secret": secret,
                "intType": intType
            },
            authorizeRequired: false
        }).then(result => {
            if (result.success) {
                postLoginMessageToExtension(result.result);
                const session = getSession();
                session.authorization = result.result.token;
                session.userName = result.result.userName;
                session.role = result.result.role;
                session.userId = result.result.userId;
                // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.LMS;
                session.idpType = IDENTITY_PROVIDER_TYPES.LMS;
                session.intType = INTEGRATION_TYPES.MOODLE;
                session.firstName = result.result.firstName ? result.result.firstName : "";
                session.lastName = result.result.lastName ? result.result.lastName : "";
                session.email = result.result.email ? result.result.email : "";
                session.gradedLink = false;
                session.settings = result.result.settings ? result.result.settings : {};
                if(result.result.courseId) {
                    session.courseId = result.result.courseId;
                }
                if(result.result.currentCourseRole && result.result.currentCourseRole.includes("#")) {
                    session.currentCourseRole = result.result.currentCourseRole.split("#")[1];
                } else {
                    session.currentCourseRole = "";
                }
                setSession(session);
                if(result.result.tpCourseId && result.result.tpCourseId !== "") {
                    const session = getSession();
                    session.tpCourseId = result.result.tpCourseId;
                    setSession(session);
                    // Roster sync now happens server-side inside loginLMS for every LMS type, so no
                    // separate createUserRoster call is needed here.
                }
                if(reqType && reqType === "deepLinking") {
                    history.push({pathname: POLL_CHOOSER_PATH + session.courseId, state : { secret : secret, intType : INTEGRATION_TYPES.MOODLE }});
                } else if(reqType && reqType === "pollResponseRedirect" && redirectURL !== "null") {
                    const session = getSession();
                    session.gradedLink = true;
                    if(session.currentCourseRole === ROLES.VIEWER.value || session.role === ROLES.VIEWER.value) {
                        setSession(session);
                        storeLTIGradebookRequest(secret, redirectURL.substring(0, 6));
                        setUserNameToLocalStorage(redirectURL.substring(0, 6), result.result.userId, VIEWER_USER_TYPE.LMS_MOODLE);
                        history.push({pathname: "/join/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    } else{
                        setSession(session);
                        history.push({pathname: "/poll/share/" + redirectURL.slice(-36) + "/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    }
                } else {
                    if (location.state && location.state.from) {
                        history.push(location.state.from);
                    } else {
                        history.push(HOME_PATH);
                    }
                }
            } else {
                notifyError(result.errorMessage);
            }
        }).catch(err => {
            console.log(err);
            notifyError(t('auth.engageRedirectError'));
            return false;
        }).finally(() => {
            setLoading(false);
        })
    }

    function loginD2L(secret, intType, reqType, redirectURL) {
        setLoading(true);
        return fetchWrapper.post({
            // pass dropdown value as last path parameter instead of hard-coded ADFS value
            url: `${config.api.authentication}/login/lms`,
            body: {
                "secret": secret,
                "intType": intType
            },
            authorizeRequired: false
        }).then(result => {
            if (result.success) {
                postLoginMessageToExtension(result.result);
                const session = getSession();
                session.authorization = result.result.token;
                session.userName = result.result.userName;
                session.role = result.result.role;
                session.userId = result.result.userId;
                // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.LMS;
                session.idpType = IDENTITY_PROVIDER_TYPES.LMS;
                session.intType = INTEGRATION_TYPES.D2L;
                session.firstName = result.result.firstName ? result.result.firstName : "";
                session.lastName = result.result.lastName ? result.result.lastName : "";
                session.email = result.result.email ? result.result.email : "";
                session.gradedLink = false;
                session.settings = result.result.settings ? result.result.settings : {};
                if(result.result.courseId) {
                    session.courseId = result.result.courseId;
                }
                if(result.result.currentCourseRole && result.result.currentCourseRole.includes("#")) {
                    session.currentCourseRole = result.result.currentCourseRole.split("#")[1];
                } else {
                    session.currentCourseRole = "";
                }
                setSession(session);
                if(result.result.tpCourseId && result.result.tpCourseId !== "") {
                    const session = getSession();
                    session.tpCourseId = result.result.tpCourseId;
                    setSession(session);
                    // Roster sync now happens server-side inside loginLMS for every LMS type, so no
                    // separate createUserRoster call is needed here.
                }
                if(reqType && reqType === "deepLinking") {
                    history.push({pathname: POLL_CHOOSER_PATH + session.courseId, state : { secret : secret, intType : INTEGRATION_TYPES.D2L }});
                } else if(reqType && reqType === "pollResponseRedirect" && redirectURL !== "null") {
                    const session = getSession();
                    session.gradedLink = true;
                    if(session.currentCourseRole === ROLES.VIEWER.value || session.role === ROLES.VIEWER.value) {
                        storeLTIGradebookRequest(secret, redirectURL.substring(0, 6));
                        setSession(session);
                        setUserNameToLocalStorage(redirectURL.substring(0, 6), result.result.userId, VIEWER_USER_TYPE.LMS_D2L);
                        history.push({pathname: "/join/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    } else if(session.role === ROLES.CREATOR.value || session.role === ROLES.IT_MANAGER.value) {
                        setSession(session);
                        history.push({pathname: "/poll/share/" + redirectURL.slice(-36) + "/" + redirectURL.substring(0, 6), state: {isLMS: true}});
                    }
                } else {
                    if (location.state && location.state.from) {
                        history.push(location.state.from);
                    } else {
                        history.push(HOME_PATH);
                    }
                }
            } else {
                notifyError(result.errorMessage);
            }
        }).catch(err => {
            console.log(err);
            notifyError(t('auth.engageRedirectError'));
            return false;
        }).finally(() => {
            setLoading(false);
        })
    }

    function storeLTIGradebookRequest(secret, pollCode) {
        const session = getSession();
        fetchWrapper.post({
            url:`${config.api.integration}/lti/gradebook/request`,
            body: {
                secret: secret,
                pollCode: pollCode,
                userId: session.userId,
                intType: session.intType
            }
        }).then(result => {
            console.log(result);
        }).catch(err => {
            console.log(err);
        })
    }

    function createUserRoster(courseId, intType, isManual = false) {
        setLoading(true);
        const session = getSession();

        return ensureLMSAuthorized(intType).then(authorized => {
          if (!authorized) {
            setLoading(false);
            return Promise.reject(Error(JSON.stringify({
              errorCode: "CANVAS_AUTH_REQUIRED",
              errorMessage: "Canvas authorization required"
            })));
          }

          return new Promise((resolve, reject) => {
            fetchWrapper
              .post({
                url: `${config.api.authentication}/user/roster`,
                body: {
                  courseId: courseId,
                  intType: intType,
                  userId: session.userId,
                  isManual: isManual
                },
              })
              .then((result) => {
                console.log(result);
              //   notifySuccess("The user roster has been successfully synced.");
                setLoading(false);
                resolve(result); // Resolve the promise on success
              })
              .catch((err) => {
                console.log(err);
                setLoading(false);
                if (JSON.parse(err.message).errorCode === "Y015") {
                  console.log(t('common.retrieveError', { entity: t('entities.course') }));
                } else if (JSON.parse(err.message).errorCode === "Y030") {
                  console.log(t('roster.apiConfigError'));
                } else if (JSON.parse(err.message).errorCode === "Y031") {
                  console.log(t('roster.apiResponseError'));
                } else {
                  console.log("Unable to sync User Roster");
                }
                reject(err); // Reject the promise on error
              });
          });
        });
      }

    // Read-only "Test API" check - does not sync/save anything, just reports whether the LMS's
    // course-info and roster calls succeed for the given lmsProvidedCourseId.
    function testLmsApiAccess(intType, lmsProvidedCourseId) {
        return ensureLMSAuthorized(intType).then(authorized => {
            if (!authorized) {
                return null;
            }

            return fetchWrapper.post({
                url: `${config.api.authentication}/lms/test-access`,
                body: {
                    intType,
                    lmsProvidedCourseId,
                },
            }).then(result => {
                if (!result.success) {
                    notifyError(t('integration.testApiAccessError'));
                    return null;
                }
                return result.result;
            }).catch(err => {
                console.log("Error testing LMS API access.", err);
                notifyError(t('integration.testApiAccessError'));
                return null;
            });
        });
    }

    function getD2LAuthURL() {
        setLoading(true);
        fetchWrapper.get({
            url:`${config.api.authentication}/auth/d2l`,
        }).then(result => {
            console.log(result);
            window.open(result.result.authURL, "_blank");
        }).catch(err => {
            console.log(err);
            notifyError(t("auth.d2lLoginUnreachable"))
        }).finally(() => {
            setLoading(false);
        })
    }

    function tryCanvasURL(apiAccessToken) {
        setLoading(true);
        fetchWrapper.get({
            url:`https://panotesting.instructure.com/api/v1/courses/635`,
            token: apiAccessToken
        }).then(result => {
            console.log(result);
        }).catch(err => {
            console.log(err);
            notifyError(t("auth.d2lLoginUnreachable"))
        }).finally(() => {
            setLoading(false);
        })
    }


    function getAuthCount() {
        return fetchWrapper.get({
            url: `${config.api.authentication}/authCount`
        }).then(res => {
            if (res.success) {
                return res.result;
            } else {
                notifyError(res.errorMessage);
                return 0;
            }
        }).catch(err => {
            console.log(err);
            return 0;
        })
    }


    function switchInst(shortName) {
        return fetchWrapper.post({
            url: `${config.api.authentication}/auth/csm/switch`,
            authorizeRequired: true,
            body: {toShortName: shortName}
        }).then(res => {
            const {result="", success=true, errorMessage} = res;
            if (!success) {
                notifyError(errorMessage);
                return null;
            }
            return result;

        }).catch(e => {
            console.log(e);
            notifyError(t("auth.switchInstitutionInvalidSession"));
            return null;
        });
    }


    function loginCSM(secret, path) {
        return fetchWrapper.post({
            url: `${config.api.authentication}/login/csm`,
            body: {secret: secret},
            authorizeRequired: false
        }).then(res => {
            const {result={}, success=true, errorMessage} = res;
            if (!success) {
                history.push(LOGIN_PATH, {msg: errorMessage});
                return false;
            }

            const {userName, token, role, userId, firstName, lastName, email, idpType, settings} = result;
            if (role !== ROLES.VIEWER.value) {
                postLoginMessageToExtension(result);
            }


            const session = getSession();
            session.authorization = token;
            session.userName = userName;
            session.role = role;
            session.userId = userId;
            session.firstName = firstName ? firstName : "";
            session.lastName = lastName ? lastName : "";
            session.email = email ? email : "";
            // session.idpType = result.result.idpType !== "" ? result.result.idpType : IDENTITY_PROVIDER_TYPES.YUJA;
            session.isYuJaAuto = idpType === "YuJa_Auto";
            session.idpType = IDENTITY_PROVIDER_TYPES.YUJA;
            session.gradedLink = false;
            session.settings = settings ? settings : {};
            if (result.hasOwnProperty("isFromCSM")) {
                session.isFromCSM = result.isFromCSM;
            }

            if (result.hasOwnProperty("isCSMAdmin")) {
                session.isCSMAdmin = result.isCSMAdmin;
            }

            if (result.hasOwnProperty("MGMTSettings")) {
                session.MGMTSettings = result.MGMTSettings;
            }


            // console.log(session);
            setSession(session);
            if (isCSMSystem()) {
                if (!path) {
                    history.push(CSM_HOME_PATH);
                } else {
                    history.push(path);
                }
            } else {
                history.push(HOME_PATH);
            }
        }).catch(err => {
            console.log("CSM login error", err);
            history.push(LOGIN_PATH, {msg: t('auth.generalLoginError')});
            return false;
        });
    }


    function saveMGMTSettings(settings={}) {
        return fetchWrapper.put({
            url: `${config.api.authentication}/mgmt/settings`,
            authorizeRequired: true,
            body: settings,
        }).then(res => {
            const {success=true, errorMessage} = res;
            if (!success) {
                notifyError(errorMessage);
                return false;
            }
            notifySuccess(t("institution.adminPatternsSaved"));
            return true;

        }).catch(e => {
            console.log(e);
            notifyError(t("auth.switchInstitutionError"));
            return false;
        });
    }

    function countLMSLaunch(intType) {
        return fetchWrapper.get({
            url: `${config.api.authentication}/lms/launchCount/${intType}`,
            authorizeRequired: true,
        }).then(res => {
            const {success=true, errorMessage} = res;
            if (!success) {
                console.log(errorMessage);
                return 0;
            } else {
                return res.result;
            }
        }).catch(e => {
            console.error(e);
            return 0;
        });
    }

}
